/**
 * Economía del jugador: monedas, gemas y experiencia/nivel.
 *
 * Todas las funciones son puras: reciben un `MetaState` y devuelven uno
 * nuevo, nunca mutan el original. Así se pueden testear con Vitest sin
 * Phaser ni localStorage de por medio (regla de oro del plan).
 */

import type { MetaState } from '../core/types';

/**
 * Experiencia necesaria para subir del nivel `level` al siguiente.
 * Curva suave (no exponencial agresiva): a nivel 1 pide 80 XP, a nivel 10
 * pide ~1440 XP.
 */
export function xpToNextLevel(level: number): number {
  return Math.round(80 * level ** 1.3);
}

/** Suma monedas al meta (el resultado nunca baja de 0). */
export function addCoins(meta: MetaState, amount: number): MetaState {
  if (amount === 0) return meta;
  return { ...meta, coins: Math.max(0, meta.coins + amount) };
}

/** Suma gemas al meta (el resultado nunca baja de 0). Reservadas para roadmap. */
export function addGems(meta: MetaState, amount: number): MetaState {
  if (amount === 0) return meta;
  return { ...meta, gems: Math.max(0, meta.gems + amount) };
}

/**
 * Intenta gastar monedas. Devuelve `null` si no alcanza (el llamador decide
 * qué hacer, p. ej. no cerrar el modal de compra).
 */
export function spendCoins(meta: MetaState, amount: number): MetaState | null {
  if (amount < 0) throw new Error('spendCoins: el monto no puede ser negativo');
  if (meta.coins < amount) return null;
  return { ...meta, coins: meta.coins - amount };
}

/** Intenta gastar gemas. Devuelve `null` si no alcanza. */
export function spendGems(meta: MetaState, amount: number): MetaState | null {
  if (amount < 0) throw new Error('spendGems: el monto no puede ser negativo');
  if (meta.gems < amount) return null;
  return { ...meta, gems: meta.gems - amount };
}

/**
 * Suma experiencia y sube de nivel tantas veces como corresponda (soporta
 * subir varios niveles de golpe si la recompensa es grande).
 */
export function addXp(meta: MetaState, amount: number): MetaState {
  if (amount <= 0) return meta;
  let xp = meta.xp + amount;
  let level = meta.level;
  let needed = xpToNextLevel(level);
  while (xp >= needed) {
    xp -= needed;
    level += 1;
    needed = xpToNextLevel(level);
  }
  return { ...meta, xp, level };
}
